// Scroll scenes: a section taller than the screen whose stage sticks (position: sticky) while you scroll through it.
// Its progress, 0 at the top to 1 when it's about to leave, eases towards where you really are every frame, so the
// motion glides even on a notchy wheel, and goes to the scene's function. Plain scrolling stays plain: nothing hijacks
// the wheel. Under "reduce motion" the progress jumps straight to where you are.
import { createContext, useContext, useEffect, useRef } from 'react';

type Fn = (p: number) => void;
interface Scene {
  el: HTMLElement;
  fn: Fn;
  /** how quickly it catches up (per second) */
  k: number;
  p: number;
  raw: number;
  start: number;
  end: number;
  ran: boolean;
}

/** "reduce motion": the scenes hold one good frame each instead of playing */
export const REDUCE = typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;
export const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);
/** where v sits between a and b, 0–1 */
export const span = (v: number, a: number, b: number) => clamp01((v - a) / (b - a));
export const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
export const ease = {
  inOut: (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2),
  out: (t: number) => 1 - (1 - t) ** 3,
  in: (t: number) => t * t * t,
  sine: (t: number) => (1 - Math.cos(Math.PI * t)) / 2,
  /** a sheet of paper landing: a touch past, then settling */
  land: (t: number) => {
    const c1 = 1.4;
    const c3 = c1 + 1;
    return t <= 0 ? 0 : t >= 1 ? 1 : 1 + c3 * (t - 1) ** 3 + c1 * (t - 1) ** 2;
  },
};

export class Scenes {
  private list: Scene[] = [];
  private raf = 0;
  private last = 0;
  private ro: ResizeObserver;
  readonly reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;

  constructor(readonly scroller: HTMLElement) {
    this.ro = new ResizeObserver(() => this.measure());
    this.ro.observe(scroller);
    if (scroller.firstElementChild) this.ro.observe(scroller.firstElementChild);
    this.raf = requestAnimationFrame(this.tick);
  }

  add(el: HTMLElement, fn: Fn, k = 9) {
    const s: Scene = { el, fn, k, p: 0, raw: 0, start: 0, end: 1, ran: false };
    this.list.push(s);
    this.ro.observe(el);
    this.measure();
    return () => {
      this.list = this.list.filter((x) => x !== s);
      this.ro.unobserve(el);
    };
  }

  measure() {
    const sc = this.scroller;
    const top = sc.scrollTop;
    const vh = sc.clientHeight;
    const base = sc.getBoundingClientRect().top;
    for (const s of this.list) {
      const y = s.el.getBoundingClientRect().top - base + top;
      s.start = y;
      s.end = y + Math.max(1, s.el.offsetHeight - vh);
    }
  }

  private tick = (t: number) => {
    const dt = Math.min(0.05, this.last ? (t - this.last) / 1000 : 0.016);
    this.last = t;
    const top = this.scroller.scrollTop;
    for (const s of this.list) {
      s.raw = clamp01((top - s.start) / (s.end - s.start));
      const a = this.reduce ? 1 : 1 - Math.exp(-s.k * dt);
      let p = s.p + (s.raw - s.p) * a;
      if (Math.abs(s.raw - p) < 0.0004) p = s.raw;
      if (p !== s.p || !s.ran) {
        s.p = p;
        s.ran = true;
        s.fn(p);
      }
    }
    this.raf = requestAnimationFrame(this.tick);
  };

  /** scroll so the scene `el` is at progress p */
  goTo(el: HTMLElement, p = 0) {
    const s = this.list.find((x) => x.el === el);
    const y = s ? s.start + (s.end - s.start) * p : el.offsetTop;
    this.scroller.scrollTo({ top: y, behavior: this.reduce ? 'auto' : 'smooth' });
  }

  dispose() {
    cancelAnimationFrame(this.raf);
    this.ro.disconnect();
  }
}

export const ScenesCtx = createContext<Scenes | null>(null);

/** run `fn(progress)` as the section `ref` scrolls through */
export function useScene(ref: React.RefObject<HTMLElement | null>, fn: Fn, k?: number) {
  const scenes = useContext(ScenesCtx);
  const f = useRef(fn);
  f.current = fn;
  useEffect(() => {
    if (!scenes || !ref.current) return;
    return scenes.add(ref.current, (p) => f.current(p), k);
  }, [scenes, ref, k]);
}

/** fetch a JSON file once (the paper maps), shared by everyone who asks */
const jsonCache = new Map<string, Promise<unknown>>();
export function loadJson<T>(url: string): Promise<T> {
  if (!jsonCache.has(url))
    jsonCache.set(
      url,
      fetch(url).then((r) => {
        if (!r.ok) throw new Error(`${url}: ${r.status}`);
        return r.json();
      }),
    );
  return jsonCache.get(url) as Promise<T>;
}
